/**
 * Design-board route (orca-admin) — eagerly registers the file route with
 * its production gate, search validation and head stylesheets; the frame
 * registry and all 51 board components are deferred to the `.lazy.tsx` twin
 * so the frame components never reach the always-loaded main chunk. The
 * scoped capture CSS is linked from `head()` (see
 * {@link designBoardStylesheetLinks}), not imported by the lazy twin.
 *
 * The board renders the frozen design capture in docs/design/orca-admin for
 * the pixel-convergence loop (scripts/design-board-sweep.ts) and for
 * eyeballing frames during /design-wire. It is not a product surface:
 * production requests 404 via `beforeLoad`, while dev (`development`) and
 * preview (`preview`) keep it reachable from the same build artifact.
 *
 * @since 1.0.0
 */
import { createFileRoute, notFound } from "@tanstack/react-router";

import fontsCss from "~/components/design/orca-admin/fonts.css?url";
import { ORCA_ADMIN_STYLESHEET_LINKS } from "~/components/design/orca-admin/stylesheets.ts";
import type { HeadLink } from "~/config/head.ts";
import { runtimeNodeEnv, shouldHideDesignBoard, validateDesignBoardSearch } from "~/lib/design-board.ts";

/**
 * The board's stylesheets as render-blocking head links, in the board's own
 * load order: the board-only `fonts.css` (the capture's Google Fonts request,
 * so rasterization matches the frozen PNGs) first, then the slug's scoped
 * capture sheets. Linked here rather than side-effect-imported from the
 * design components so the sheets are in the SSR head (no flash of unstyled
 * frames) and so `fonts.css` never ships with the production `/orca` pages,
 * which load their fonts through `config/head.ts`.
 *
 * @since 1.0.0
 */
export const designBoardStylesheetLinks: Array<HeadLink> = [
  { rel: "stylesheet", href: fontsCss },
  ...ORCA_ADMIN_STYLESHEET_LINKS,
];

export const Route = createFileRoute("/design-board/orca-admin")({
  beforeLoad: () => {
    if (shouldHideDesignBoard(runtimeNodeEnv())) {
      throw notFound();
    }
  },
  validateSearch: validateDesignBoardSearch,
  head: () => ({ links: designBoardStylesheetLinks }),
});
